import { useEffect, useRef, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import { Draft, SharingDraft } from '@/types';
import { getParams, isAdminMode } from '@/utils/params';
import { isSameDraft } from '@/model/draft/isSameDraft';
import { mapSharingDraftToDraftWithRedux } from '@/model/draftMapper/mapSharingDraftToDraftWithRedux';
import { getCore } from '@/redux/core/getters/getCore';
import { getDraft } from '@/redux/draft/getters/getDraft';
import { loadFromStorage } from '@/redux/draft/memento/loadFromStorage';
import { startWritingToMemento } from '@/redux/draft/memento/status';
import { StoreState } from '@/redux/types';

type UseMementoReturnType = [(() => void)?, Draft?, SharingDraft?];

export function useMemento() {
  const store = useStore<StoreState>();
  const mementoCheckEnabled = useSelector(
    (state: StoreState) => getCore(state).mementoCheckEnabled
  );

  const [returnValue, setReturnValue] = useState<UseMementoReturnType>([]);
  const mementoWasShown = useRef(false);

  useEffect(() => {
    if (!mementoCheckEnabled) {
      return;
    }

    const { platform, contextId, basketId, basketItemId } = getParams();
    if (mementoWasShown.current || isAdminMode() || (basketId && basketItemId)) {
      return;
    }

    const ignoreErrorButLogInDevelopment = (error: unknown) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error during memento initialization.', error);
      }
    };

    mementoWasShown.current = true;

    try {
      const sharingDraft = loadFromStorage(contextId, platform);

      if (
        !sharingDraft ||
        !sharingDraft.configurations.length ||
        isSameDraft(sharingDraft, getDraft(store.getState()))
      ) {
        startWritingToMemento();
        return;
      }

      mapSharingDraftToDraftWithRedux(sharingDraft, store.getState(), store.dispatch).then(
        (draft) => {
          setReturnValue([() => setReturnValue([]), draft, sharingDraft]);
        },
        (error) => {
          ignoreErrorButLogInDevelopment(error);
          startWritingToMemento();
        }
      );
    } catch (error) {
      ignoreErrorButLogInDevelopment(error);
    }
  }, [store, mementoCheckEnabled]);

  return returnValue;
}
